import { CalendarOff, Clock, MoreHorizontal, Scissors } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";

type PushType = "hours" | "holidays" | "services";

const ICON_MAP: Record<PushType, React.ComponentType<{ className?: string }>> = {
  hours: Clock,
  holidays: CalendarOff,
  services: Scissors,
};

interface PushSyncToggleCardProps {
  /** Which type of data this card controls. Determines the icon. */
  pushType: PushType;
  /** i18n key prefix under `settings:reviews` namespace */
  i18nKey: string;
  /** Current value of the toggle. */
  enabled: boolean;
  /** Fired on toggle. Hook is responsible for the actual mutation + toast. */
  onChange: (enabled: boolean) => void;
  /** True while the toggle mutation is in flight. */
  isPending: boolean;
  /** True if the current user is an admin (renders three-dot menu). */
  isAdmin: boolean;
  /** Triggered when admin clicks "Sync now". */
  onSyncNow: () => void;
  /** True while the sync is in progress. */
  isSyncing: boolean;
}

export function PushSyncToggleCard({
  pushType,
  i18nKey,
  enabled,
  onChange,
  isPending,
  isAdmin,
  onSyncNow,
  isSyncing,
}: PushSyncToggleCardProps) {
  const { t } = useTranslation("settings");
  const Icon = ICON_MAP[pushType];

  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="rounded-md bg-indigo-50 dark:bg-indigo-950 p-2 shrink-0">
          <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="flex-1 min-w-0">
          <label
            htmlFor={`gbp-push-${pushType}`}
            className="font-medium text-sm cursor-pointer block"
          >
            {t(`reviews.${i18nKey}.title`)}
          </label>
          <p className="text-xs text-muted-foreground mt-0.5">
            {enabled
              ? t(`reviews.${i18nKey}.descriptionOn`)
              : t(`reviews.${i18nKey}.descriptionOff`)}
          </p>
        </div>
        <Switch
          id={`gbp-push-${pushType}`}
          checked={enabled}
          onCheckedChange={onChange}
          disabled={isPending}
          aria-label={t(`reviews.${i18nKey}.title`)}
        />
        {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" disabled={isSyncing}>
                <MoreHorizontal className="w-4 h-4" />
                <span className="sr-only">{t("reviews.syncNow")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onSyncNow} disabled={isSyncing}>
                {t("reviews.syncNow")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </CardContent>
    </Card>
  );
}
